import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

const COLUMNS = [
  { key: "id",                     label: "ID",               editable: false, width: 80  },
  { key: "sku_object",             label: "SKU Object ID",    editable: false, width: 120 },
  { key: "sku",                    label: "SKU",              editable: false, width: 140 },
  { key: "sku_code",               label: "SKU (From Object)",editable: false, width: 160 },
  { key: "sku_name",               label: "SKU Name",         editable: false, width: 220 },
  { key: "mp_name",                label: "MP Name",          editable: true,  width: 140 },
  { key: "product_cost_tag",       label: "Cost Tag",         editable: true,  width: 130 },
  { key: "category",               label: "Category",         editable: false, width: 160 },
  { key: "sku_total_cost",         label: "SKU Cost",         editable: false, width: 120 },
  { key: "sku_total_cost_tax",     label: "SKU Tax",          editable: false, width: 130 },
  { key: "packaging_total_w_tax",  label: "Packaging Cost",   editable: false, width: 140 },
  { key: "other_cost_total_w_tax", label: "Other Cost Total", editable: false, width: 150 },
  { key: "mile_1_cost_w_tax",      label: "1 Mile Cost",      editable: false, width: 120 },
  { key: "mile_2_cost_w_tax",      label: "2 Mile Cost",      editable: false, width: 120 },
  { key: "processing_cost_w_tax",  label: "Processing Cost",  editable: false, width: 130 },
  { key: "total_pd_cost",          label: "Total PD Cost",    editable: false, width: 140 },
  { key: "logistic_charges",       label: "Logistics",        editable: true,  width: 120 },
  { key: "commission",             label: "Commission",       editable: true,  width: 120 },
  { key: "returns",                label: "Returns",          editable: true,  width: 110 },
  { key: "capital_charges",        label: "Capital",          editable: true,  width: 120 },
  { key: "other_cost",             label: "Other Cost",       editable: true,  width: 120 },
  { key: "marketing_cost",         label: "Marketing",        editable: true,  width: 120 },
  { key: "primary_sp",             label: "Primary SP",       editable: true,  width: 110 },
  { key: "cm1",                    label: "CM1",              editable: false, width: 120 },
  { key: "secondary_sp",           label: "Secondary SP",     editable: true,  width: 130 },
  { key: "mrp",                    label: "MRP",              editable: false, width: 110 },
  { key: "status",                 label: "Status",           editable: true,  badge: true, width: 120 },
  { key: "status_updated_date",    label: "Status Date",      editable: false, width: 130 },
  { key: "target_date",            label: "Target Date",      editable: false, width: 130 },
  { key: "created_at",             label: "Created At",       editable: false, width: 180 },
  { key: "updated_at",             label: "Updated At",       editable: false, width: 180 },
];

const DEFAULT_VISIBLE = [
  "id", "sku", "sku_object", "sku_name", "mrp", "category", "mp_name",
  "product_cost_tag", "total_pd_cost", "commission", "logistic_charges",
  "returns", "capital_charges", "other_cost", "marketing_cost",
  "primary_sp", "cm1", "secondary_sp", "status", "status_updated_date", "target_date",
];

const DEFAULT_FILTERS = { search: "", sku: "", mp_name: "", status: "" };

const REQUIRED_COLUMN_KEYS = new Set([
  "mp_name", "product_cost_tag", "logistic_charges", "commission",
  "returns", "capital_charges", "other_cost", "marketing_cost",
  "primary_sp", "secondary_sp", "status",
]);

const PERCENTAGE_VIEW_COLUMNS = new Set([
  "sku_total_cost", "sku_total_cost_tax", "packaging_total_w_tax", "other_cost_total_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "logistic_charges", "commission", "returns", "capital_charges",
  "other_cost", "marketing_cost", "secondary_sp", "cm1", "mrp",
]);

const DECIMAL_FIELDS = new Set([
  "logistic_charges", "commission", "returns", "capital_charges",
  "other_cost", "marketing_cost", "primary_sp", "secondary_sp",
]);

const EDITABLE_KEYS = COLUMNS.filter(c => c.editable).map(c => c.key);

const CSV_HELPER_FIELDS = ["id", "sku", "sku_object", "sku_code", "business_type", "cost_tag"];
const IMPORTABLE_FIELDS = new Set([...CSV_HELPER_FIELDS, ...EDITABLE_KEYS]);

const CSV_DOWNLOAD_COLUMNS = [
  { key: "sku_code",               header: "SKU" },
  { key: "sku_name",               header: "SKU Name" },
  { key: "mrp",                    header: "MRP" },
  { key: "category",               header: "Category" },
  { key: "mp_name",                header: "MP Name*" },
  { key: "product_cost_tag",       header: "Cost Tag*" },
  { key: "sku_total_cost_tax",     header: "SKU Cost+Tax" },
  { key: "packaging_total_w_tax",  header: "Packaging Cost" },
  { key: "other_cost_total_w_tax", header: "Other Cost Total" },
  { key: "logistic_charges",       header: "Logistics*" },
  { key: "commission",             header: "Commission*" },
  { key: "returns",                header: "Returns*" },
  { key: "capital_charges",        header: "Capital*" },
  { key: "other_cost",             header: "Other Cost*" },
  { key: "marketing_cost",         header: "Marketing*" },
  { key: "primary_sp",             header: "Primary SP*" },
  { key: "secondary_sp",           header: "Secondary SP*" },
  { key: "status",                 header: "Status*" },
  { key: "status_updated_date",    header: "Status Date" },
  { key: "target_date",            header: "Target Date" },
  { key: "created_at",             header: "Created At" },
  { key: "updated_at",             header: "Updated At" },
];
const CSV_DOWNLOAD_KEYS = CSV_DOWNLOAD_COLUMNS.map(c => c.key);

const CSV_SAMPLE_COLUMNS = [
  "sku", "cost_tag", "mp_name", "logistic_charges", "commission",
  "returns", "capital_charges", "other_cost", "marketing_cost",
  "primary_sp", "secondary_sp", "status",
];
const CSV_SAMPLE_ROWS = [
  {
    sku: "SKU-ABC-001", cost_tag: "b2c", mp_name: "amazon",
    logistic_charges: "55.00", commission: "18.00", returns: "2.50",
    capital_charges: "1.50", other_cost: "4.25", marketing_cost: "6.00",
    primary_sp: "499.00", secondary_sp: "459.00", status: "approved",
  },
];

const CSV_HEADER_ALIASES = {
  sku: "sku", sku_object: "sku_object", "sku object": "sku_object",
  sku_object_id: "sku_object", "sku object id": "sku_object",
  "sku code": "sku_code", sku_code: "sku_code",
  "mp name": "mp_name", mp_name: "mp_name",
  "cost tag": "cost_tag", cost_tag: "cost_tag",
  business_type: "business_type",
  logistics: "logistic_charges", logistic_charges: "logistic_charges",
  commission: "commission", returns: "returns",
  capital: "capital_charges", capital_charges: "capital_charges",
  "other cost": "other_cost", other_cost: "other_cost",
  marketing: "marketing_cost", marketing_cost: "marketing_cost",
  "primary sp": "primary_sp", primary_sp: "primary_sp",
  "secondary sp": "secondary_sp", secondary_sp: "secondary_sp",
  status: "status",
  "status date": "status_updated_date", status_updated_date: "status_updated_date",
  "target date": "target_date", target_date: "target_date",
  id: "id",
};

const STAR_UPDATE_FIELDS = [
  "mp_name", "product_cost_tag", "logistic_charges", "commission",
  "returns", "capital_charges", "other_cost", "marketing_cost",
  "primary_sp", "secondary_sp", "status",
];

// ─── API helpers ──────────────────────────────────────────────────────────────

function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch { return {}; }
}

function parseListResponse(data) {
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

async function fetchRows({ page, pageSize, filters }) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (filters.search)  params.set("search", filters.search);
  if (filters.sku)     params.set("sku", filters.sku);
  if (filters.mp_name) params.set("mp_name", filters.mp_name);
  if (filters.status)  params.set("status", filters.status);
  const response = await fetch(`${BASE_URL}/unit-economics/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch unit economics");
  return data;
}

async function fetchDropdownOptions(fieldName) {
  const params = new URLSearchParams({ field_name: fieldName });
  const response = await fetch(`${BASE_URL}/dropdowns/options/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Failed to fetch ${fieldName} options`);
  return parseListResponse(data)
    .map(row => ({ v: String(row?.value ?? "").trim(), l: String(row?.label ?? row?.value ?? "").trim() }))
    .filter(row => row.v);
}

async function fetchDropdownWithFallback(fieldNames) {
  for (const name of fieldNames) {
    try {
      const opts = await fetchDropdownOptions(name);
      if (opts.length) return opts;
    } catch { /* try next */ }
  }
  return [];
}

async function bulkUpdate(payload) {
  const response = await fetch(`${BASE_URL}/unit-economics/bulk-update/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Bulk update failed");
  return data?.data || data;
}

async function bulkCreate(payload) {
  const response = await fetch(`${BASE_URL}/unit-economics/bulk-create/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Bulk create failed");
  return data?.data || data;
}

// ─── CSV utilities ────────────────────────────────────────────────────────────

function csvSafe(v) {
  const s = String(v ?? "");
  return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows, fields, headerMap = null) {
  const header = fields.map(f => csvSafe(headerMap?.[f] || f)).join(",");
  const body = rows.map(r => fields.map(f => csvSafe(r[f])).join(",")).join("\n");
  return `${header}\n${body}`;
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const rows = []; let cur = "", line = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nx = text[i + 1];
    if (ch === '"' && inQ && nx === '"') { cur += '"'; i++; continue; }
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { line.push(cur); cur = ""; continue; }
    if ((ch === "\n" || ch === "\r") && !inQ) {
      if (ch === "\r" && nx === "\n") i++;
      line.push(cur); rows.push(line); line = []; cur = ""; continue;
    }
    cur += ch;
  }
  if (cur.length > 0 || line.length > 0) { line.push(cur); rows.push(line); }
  return rows;
}

// ─── Row computation ──────────────────────────────────────────────────────────

function toNum(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function computeCm1(row) {
  const primarySp = toNum(row.primary_sp);
  if (primarySp == null) return null;
  const costFields = [
    "sku_total_cost_tax", "packaging_total_w_tax", "other_cost_total_w_tax",
    "logistic_charges", "commission", "returns", "capital_charges", "other_cost", "marketing_cost",
  ];
  const totalCost = costFields.reduce((sum, key) => sum + (toNum(row[key]) || 0), 0);
  return (primarySp - totalCost).toFixed(2);
}

function computeTotalPdCost(row) {
  const keys = [
    "sku_total_cost", "sku_total_cost_tax", "packaging_total_w_tax",
    "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  ];
  return keys.reduce((sum, key) => sum + (toNum(row[key]) || 0), 0).toFixed(2);
}

function enrichRow(row) {
  return { ...row, cm1: computeCm1(row), total_pd_cost: computeTotalPdCost(row) };
}

function toPct(value, base) {
  const n = toNum(value), b = toNum(base);
  if (n == null || b == null || b === 0) return "—";
  return `${((n / b) * 100).toFixed(2)}%`;
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// ─── Shared style ─────────────────────────────────────────────────────────────

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = memo(({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const wrapperCls = {
    success: "border-green-500 bg-green-50 text-green-800",
    error:   "border-red-500 bg-red-50 text-red-800",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-800",
    info:    "border-blue-500 bg-blue-50 text-blue-800",
  };
  const dotCls = { success: "bg-green-500", error: "bg-red-500", warning: "bg-yellow-500", info: "bg-blue-500" };
  return (
    <div className={cn(
      "fixed bottom-6 right-6 z-[9999] flex items-center gap-3 max-w-sm",
      "rounded-lg border-2 px-4 py-3 shadow-xl text-sm",
      wrapperCls[type] || wrapperCls.info
    )}>
      <span className={cn("w-2 h-2 rounded-full shrink-0", dotCls[type] || dotCls.info)} />
      <span className="flex-1">{message}</span>
      <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
});

// ─── EditableCell ─────────────────────────────────────────────────────────────

const EditableCell = memo(({ value, rowId, colKey, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== String(value ?? "")) onChange(rowId, colKey, draft);
  }, [draft, value, rowId, colKey, onChange]);

  if (editing) return (
    <Input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setDraft(String(value ?? "")); setEditing(false); }
      }}
      className="h-7 text-xs py-0 px-1.5"
    />
  );

  return (
    <span
      onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
      title="Click to edit"
      className={cn(
        "block w-full cursor-text truncate px-0.5 py-0.5 rounded text-sm transition-colors hover:bg-accent",
        value ? "text-foreground" : "text-muted-foreground"
      )}
    >
      {String(value ?? "—")}
    </span>
  );
});

// ─── SortIcon ─────────────────────────────────────────────────────────────────

function SortIcon({ dir }) {
  if (dir === "asc")  return <ArrowUp   className="w-3 h-3 text-primary shrink-0" />;
  if (dir === "desc") return <ArrowDown className="w-3 h-3 text-primary shrink-0" />;
  return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40 shrink-0" />;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  approved: "bg-blue-100 text-blue-700 border-blue-200",
  draft:    "bg-yellow-100 text-yellow-700 border-yellow-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const label = String(value).replace(/_/g, " ");
  const style = STATUS_STYLES[String(value).toLowerCase()];
  if (!style) return <span className="text-muted-foreground text-xs capitalize">{label}</span>;
  return <Badge className={cn("capitalize text-xs font-medium", style)}>{label}</Badge>;
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────

const ResizeHandle = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors"
  />
);

// ─── ColumnPicker ─────────────────────────────────────────────────────────────

const ColumnPicker = memo(({ all, visible, onToggle, onSelectAll, onReset }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-muted-foreground">
        Visible: <strong className="text-foreground">{visible.length}</strong> / {all.length}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onSelectAll}>All</Button>
        <Button variant="outline" size="sm" onClick={onReset}>Reset</Button>
      </div>
    </div>
    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
      {all.map(col => {
        const on = visible.includes(col.key);
        return (
          <label
            key={col.key}
            className={cn(
              "flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded-lg text-sm border transition-all",
              on
                ? "bg-violet-100 text-violet-700 border-violet-300"
                : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted"
            )}
          >
            <Checkbox
              checked={on}
              onCheckedChange={() => onToggle(col.key)}
              className={on ? "border-violet-400 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600" : ""}
            />
            {col.label}{REQUIRED_COLUMN_KEYS.has(col.key) ? " *" : ""}
          </label>
        );
      })}
    </div>
  </div>
));

// ─── FilterPanel ──────────────────────────────────────────────────────────────

const FilterPanel = memo(({ filters, setFilters, marketplaceOptions, statusOptions, onApply, onClear }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SKU</label>
        <Input
          placeholder="Exact SKU"
          value={filters.sku}
          onChange={e => setFilters(p => ({ ...p, sku: e.target.value }))}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">MP Name</label>
        <select value={filters.mp_name} onChange={e => setFilters(p => ({ ...p, mp_name: e.target.value }))} className={SELECT_CLS}>
          <option value="">All</option>
          {marketplaceOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</label>
        <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className={SELECT_CLS}>
          <option value="">All</option>
          {statusOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <Button size="sm" onClick={onApply}>Apply filters</Button>
      <Button size="sm" variant="outline" onClick={onClear}>Clear all</Button>
    </div>
  </div>
));

// ─── CostBreakupModal ─────────────────────────────────────────────────────────

function CostBreakupModal({ row, onClose }) {
  const items = [
    ["SKU Cost",        "sku_total_cost"],
    ["SKU Tax",         "sku_total_cost_tax"],
    ["Packaging Cost",  "packaging_total_w_tax"],
    ["1 Mile Cost",     "mile_1_cost_w_tax"],
    ["2 Mile Cost",     "mile_2_cost_w_tax"],
    ["Processing Cost", "processing_cost_w_tax"],
  ];

  return (
    <div className="fixed inset-0 z-[5001] bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-[420px] max-w-[92vw] overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground">PD Cost Breakup</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              SKU: {row.sku || "—"} | MP: {row.mp_name || "—"}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-1">
          {items.map(([label, key]) => (
            <div key={key} className="flex justify-between items-center py-2 border-b border-dashed border-border last:border-0 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-semibold text-foreground">
                {Number(toNum(row[key]) || 0).toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-border flex justify-between items-center">
          <span className="text-sm font-semibold text-foreground">Total PD Cost</span>
          <span className="text-lg font-bold text-primary">
            {Number(toNum(row.total_pd_cost) || 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── ActionsModal ─────────────────────────────────────────────────────────────

function ActionsModal({ onClose, selectedRows, appliedFilters, onUpdateSelected, onUploadImport, onUploadBulkUpdate, onDownloadSample, showMessage }) {
  const [busy, setBusy] = useState({ downloading: false, uploading: false, updating: false });
  const isBusy = Object.values(busy).some(Boolean);
  const set = k => v => setBusy(p => ({ ...p, [k]: v }));

  const dlSelected = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("downloading")(true);
    try {
      const headerMap = Object.fromEntries(CSV_DOWNLOAD_COLUMNS.map(c => [c.key, c.header]));
      downloadCsv(toCsv(selectedRows, CSV_DOWNLOAD_KEYS, headerMap), `unit_economics_selected_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${selectedRows.length} rows.`);
    } finally { set("downloading")(false); }
  };

  const dlAll = async () => {
    set("downloading")(true);
    try {
      const all = []; let pg = 1, hasNext = true;
      while (hasNext) {
        const data = await fetchRows({ page: pg, pageSize: 500, filters: appliedFilters });
        const chunk = parseListResponse(data);
        all.push(...chunk); hasNext = Boolean(data?.next); pg++;
        if (!chunk.length) break;
      }
      const headerMap = Object.fromEntries(CSV_DOWNLOAD_COLUMNS.map(c => [c.key, c.header]));
      downloadCsv(toCsv(all, CSV_DOWNLOAD_KEYS, headerMap), `unit_economics_all_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${all.length} rows.`);
    } catch (e) { showMessage(e.message || "Download failed", "error"); }
    finally { set("downloading")(false); }
  };

  const updateSel = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("updating")(true);
    try { await onUpdateSelected(); }
    catch (e) { showMessage(e.message || "Update failed", "error"); }
    finally { set("updating")(false); }
  };

  const handleFile = (handler) => async (e) => {
    const file = e.target.files?.[0];
    if (!file || isBusy) { e.target.value = ""; return; }
    set("uploading")(true);
    try { await handler(await file.text()); }
    catch (err) { showMessage(err.message || "Upload failed", "error"); }
    finally { set("uploading")(false); e.target.value = ""; }
  };

  return (
    <div className="fixed z-[5000]" style={{ top: 140, right: "max(20px, calc((100vw - 1600px) / 2 + 20px))" }}>
      <div className="bg-white rounded-2xl border border-border shadow-2xl p-6 w-[440px] max-w-[92vw]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground">Actions</h3>
          {!isBusy && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">Download, update, or import unit economics rows.</p>

        <div className="flex flex-col gap-2.5">
          <div className="flex gap-2">
            <Button className="flex-1 gap-1" onClick={dlSelected} disabled={isBusy}>
              {busy.downloading && <Loader2 className="h-3 w-3 animate-spin" />}
              {busy.downloading ? "Downloading…" : "Download Selected"}
            </Button>
            <Button variant="outline" className="flex-1 gap-1" onClick={dlAll} disabled={isBusy}>
              {busy.downloading && <Loader2 className="h-3 w-3 animate-spin" />}
              Download All
            </Button>
          </div>

          {selectedRows.length > 0 && (
            <Button onClick={updateSel} disabled={isBusy} className="w-full gap-1">
              {busy.updating && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy.updating ? "Updating…" : `Update Selected (${selectedRows.length})`}
            </Button>
          )}
        </div>

        {isBusy && (
          <div className="mt-4 flex items-center gap-2 text-sm text-primary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {busy.uploading ? "Uploading…" : busy.downloading ? "Downloading…" : "Updating…"}
          </div>
        )}

        <div className="mt-4 p-4 bg-muted/40 rounded-xl border border-dashed border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">CSV Tools</span>
            <Button size="sm" variant="outline" onClick={onDownloadSample}>Sample CSV</Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={cn(
              "flex items-center justify-center text-xs font-semibold cursor-pointer",
              "border border-blue-200 bg-blue-50 text-blue-700 rounded-lg py-2.5 px-3",
              isBusy && "opacity-50 pointer-events-none"
            )}>
              Bulk Import CSV
              <input type="file" accept=".csv,text/csv" disabled={isBusy} onChange={handleFile(onUploadImport)} className="hidden" />
            </label>
            <label className={cn(
              "flex items-center justify-center text-xs font-semibold cursor-pointer",
              "border border-border bg-white text-muted-foreground rounded-lg py-2.5 px-3",
              isBusy && "opacity-50 pointer-events-none"
            )}>
              Bulk Update CSV
              <input type="file" accept=".csv,text/csv" disabled={isBusy} onChange={handleFile(onUploadBulkUpdate)} className="hidden" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Import: creates rows without id and updates rows with id. Update: only rows with id are processed.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── DataTableRow ─────────────────────────────────────────────────────────────

const DataTableRow = memo(function DataTableRow({ row, rowIndex, columns, isSelected, onToggleRow, onCellChange, onOpenCostBreakup, percentMode }) {
  return (
    <tr className={cn(
      "border-t border-border transition-colors",
      isSelected ? "bg-violet-50" : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/50",
      !isSelected && "hover:bg-slate-50"
    )}>
      <td className="px-3 py-2 text-center w-11">
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleRow(row.id)} />
      </td>
      {columns.map(col => {
        const raw = row[col.key];
        const value = raw && typeof raw === "object" ? JSON.stringify(raw) : raw;
        const isDate = col.key.endsWith("_date") || col.key.endsWith("_at");
        const pctValue = percentMode && PERCENTAGE_VIEW_COLUMNS.has(col.key) ? toPct(value, row.primary_sp) : null;

        return (
          <td key={col.key} className="px-3 py-2 text-sm align-middle overflow-hidden">
            {col.badge ? (
              <StatusBadge value={raw} />
            ) : col.key === "total_pd_cost" ? (
              <button
                type="button"
                onClick={() => onOpenCostBreakup(row)}
                className="text-primary font-bold underline text-sm hover:text-primary/80 whitespace-nowrap"
              >
                {value ?? "—"}
              </button>
            ) : pctValue != null ? (
              <span className="text-teal-700 font-semibold whitespace-nowrap text-sm">{pctValue}</span>
            ) : col.editable ? (
              <EditableCell value={value} rowId={row.id} colKey={col.key} onChange={onCellChange} />
            ) : (
              <span className="text-muted-foreground text-xs whitespace-nowrap">
                {isDate ? fmtDate(value) : (value ?? "—")}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}, (prev, next) => (
  prev.row === next.row &&
  prev.rowIndex === next.rowIndex &&
  prev.columns === next.columns &&
  prev.isSelected === next.isSelected &&
  prev.onToggleRow === next.onToggleRow &&
  prev.onCellChange === next.onCellChange &&
  prev.onOpenCostBreakup === next.onOpenCostBreakup &&
  prev.percentMode === next.percentMode
));

// ─── DataTable ────────────────────────────────────────────────────────────────

const DataTable = memo(function DataTable({ columns, rows, loading, allChecked, selectedIds, onToggleAll, onToggleRow, onCellChange, sort, onSort, colWidths, onResizeStart, onOpenCostBreakup, percentMode }) {
  const someSelected = rows.some(r => selectedIds[r.id]);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 340px)" }}>
        <table className="border-collapse w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 44 }} />
            {columns.map(col => <col key={col.key} style={{ width: colWidths[col.key] || col.width || 120 }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-10 border-b border-border">
              <th className="px-3 py-2.5 text-center w-11">
                <Checkbox
                  checked={allChecked ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleAll}
                />
              </th>
              {columns.map(col => {
                const sorted = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => onSort(col.key)}
                    className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer select-none relative"
                  >
                    <span className="flex items-center gap-1">
                      {col.label}{REQUIRED_COLUMN_KEYS.has(col.key) ? " *" : ""}
                      <SortIcon dir={sorted ? sort.dir : null} />
                    </span>
                    <ResizeHandle onMouseDown={e => onResizeStart(e, col.key)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground text-sm">
                  No rows found. Try adjusting your filters.
                </td>
              </tr>
            ) : rows.map((row, ri) => (
              <DataTableRow
                key={row.id}
                row={row}
                rowIndex={ri}
                columns={columns}
                isSelected={!!selectedIds[row.id]}
                onToggleRow={onToggleRow}
                onCellChange={onCellChange}
                onOpenCostBreakup={onOpenCostBreakup}
                percentMode={percentMode}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ─── UnitEconomics (main page) ────────────────────────────────────────────────

export default function UnitEconomics() {
  const [rows,              setRows]              = useState([]);
  const [loading,           setLoading]           = useState(false);
  const [count,             setCount]             = useState(0);
  const [hasNextPage,       setHasNextPage]       = useState(false);
  const [hasPrevPage,       setHasPrevPage]       = useState(false);
  const [page,              setPage]              = useState(1);
  const [pageSize,          setPageSize]          = useState(50);
  const [filters,           setFilters]           = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters,    setAppliedFilters]    = useState({ ...DEFAULT_FILTERS });
  const [selectedIds,       setSelectedIds]       = useState({});
  const [visibleKeys,       setVisibleKeys]       = useState(DEFAULT_VISIBLE);
  const [marketplaceOpts,   setMarketplaceOpts]   = useState([]);
  const [statusOpts,        setStatusOpts]        = useState([{ v: "draft", l: "Draft" }, { v: "approved", l: "Approved" }, { v: "rejected", l: "Rejected" }]);
  const [sort,              setSort]              = useState(null);
  const [colWidths,         setColWidths]         = useState({});
  const [panel,             setPanel]             = useState(null); // "filters" | "columns" | null
  const [showActions,       setShowActions]       = useState(false);
  const [costBreakupRow,    setCostBreakupRow]    = useState(null);
  const [percentMode,       setPercentMode]       = useState(false);
  const [toast,             setToast]             = useState(null);

  const visibleCols   = useMemo(() => COLUMNS.filter(c => visibleKeys.includes(c.key)), [visibleKeys]);
  const selectedRows  = useMemo(() => rows.filter(r => selectedIds[r.id]), [rows, selectedIds]);
  const allChecked    = useMemo(() => rows.length > 0 && rows.every(r => selectedIds[r.id]), [rows, selectedIds]);
  const totalPages    = Math.max(1, Math.ceil(count / pageSize));

  const showMessage = useCallback((message, type = "success") => setToast({ message, type }), []);

  // Column resize
  const resizeRef = useRef(null);
  const handleResizeStart = useCallback((e, key) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[key] || COLUMNS.find(c => c.key === key)?.width || 120;
    resizeRef.current = { key, startX, startW };
    const onMove = mv => {
      const { key, startX, startW } = resizeRef.current;
      setColWidths(p => ({ ...p, [key]: Math.max(60, startW + mv.clientX - startX) }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  const handleSort = useCallback(key => {
    setSort(prev => {
      if (prev?.key === key) return prev.dir === "asc" ? { key, dir: "desc" } : null;
      return { key, dir: "asc" };
    });
    setPage(1);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRows({ page, pageSize, filters: appliedFilters });
      const list = parseListResponse(data).map(r => enrichRow(r));
      setRows(list);
      setCount(Number(data?.count || 0));
      setHasNextPage(Boolean(data?.next));
      setHasPrevPage(Boolean(data?.previous));
      setSelectedIds({});
    } catch (e) { showMessage(e.message || "Failed to load data", "error"); }
    finally { setLoading(false); }
  }, [appliedFilters, page, pageSize, showMessage]);

  const loadDropdowns = useCallback(async () => {
    const [statusRes, mpRes] = await Promise.allSettled([
      fetchDropdownOptions("status"),
      fetchDropdownWithFallback(["marketpalce", "marketplace", "mp_name"]),
    ]);
    if (statusRes.status === "fulfilled" && statusRes.value.length) setStatusOpts(statusRes.value);
    if (mpRes.status === "fulfilled") setMarketplaceOpts(mpRes.value);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  const handleCellChange = useCallback((rowId, key, val) => {
    setRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = enrichRow({ ...prev[i], [key]: val });
      return next;
    });
  }, []);

  const handleUpdateSelected = async () => {
    const payload = selectedRows.map(row => {
      const obj = { id: row.id };
      EDITABLE_KEYS.forEach(k => {
        let value = row[k];
        if (typeof value === "string") { const t = value.trim(); value = t === "" ? null : t; }
        if (DECIMAL_FIELDS.has(k)) { const t = String(value ?? "").trim(); value = t === "" ? null : t; }
        obj[k] = value ?? null;
      });
      return obj;
    });
    const res = await bulkUpdate(payload);
    showMessage(`Updated: ${Number(res.updated || 0)}, Failed: ${Number(res.failed || 0)}`, Number(res.failed || 0) ? "warning" : "success");
    await loadData();
  };

  const parseCsvRows = (text) => {
    const matrix = parseCSV(text);
    if (!matrix.length) throw new Error("CSV file is empty");
    const headers = matrix[0].map(h => {
      const normalized = String(h || "").trim().toLowerCase().replace(/\*/g, "").trim();
      return CSV_HEADER_ALIASES[normalized] || String(h || "").trim();
    });
    return matrix.slice(1)
      .filter(line => line.some(c => String(c || "").trim() !== ""))
      .map(line => {
        const obj = {};
        headers.forEach((h, i) => {
          if (!IMPORTABLE_FIELDS.has(h)) return;
          const raw = String(line[i] ?? "").trim();
          if (raw === "") { obj[h] = null; return; }
          if ((h === "sku_object" || h === "id") && /^\d+$/.test(raw)) { obj[h] = Number(raw); return; }
          obj[h] = raw;
        });
        return obj;
      });
  };

  const buildUpdateRow = (row, includeId = true) => {
    const out = {};
    if (includeId && row.id != null) out.id = row.id;
    if (row.sku_object != null) out.sku_object = row.sku_object;
    if (row.sku != null) out.sku = row.sku;
    if (row.sku_code != null) out.sku_code = row.sku_code;
    if (row.cost_tag != null) out.cost_tag = row.cost_tag;
    if (row.business_type != null) out.business_type = row.business_type;
    STAR_UPDATE_FIELDS.forEach(field => {
      let value = row[field];
      if (field === "product_cost_tag" && (value == null || value === "") && row.cost_tag != null) value = row.cost_tag;
      if (value == null || value === "") return;
      out[field] = value;
    });
    return out;
  };

  const handleImportCsv = async (text) => {
    const parsed = parseCsvRows(text);
    const updates = parsed.filter(r => r.id).map(r => buildUpdateRow(r, true));
    const creates = parsed.filter(r => !r.id).map(r => buildUpdateRow(r, false));
    let updated = 0, created = 0, failed = 0;
    if (updates.length) { const res = await bulkUpdate(updates); updated += Number(res.updated || 0); failed += Number(res.failed || 0); }
    if (creates.length) { const res = await bulkCreate(creates); created += Number(res.created || 0); failed += Number(res.failed || 0); }
    showMessage(`Updated: ${updated}, Created: ${created}, Failed: ${failed}`, failed ? "warning" : "success");
    await loadData();
  };

  const handleBulkUpdateCsv = async (text) => {
    const parsed = parseCsvRows(text);
    const updates = parsed.map(r => buildUpdateRow(r, true)).filter(r => Object.keys(r).length > 0);
    if (!updates.length) throw new Error("Bulk update CSV must include valid rows with id");
    const res = await bulkUpdate(updates);
    const skipped = parsed.length - updates.length;
    showMessage(`Updated: ${Number(res.updated || 0)}, Failed: ${Number(res.failed || 0)}${skipped ? `, Skipped: ${skipped}` : ""}`, Number(res.failed || 0) ? "warning" : "success");
    await loadData();
  };

  const handleDownloadSample = () => {
    downloadCsv(toCsv(CSV_SAMPLE_ROWS, CSV_SAMPLE_COLUMNS), "unit_economics_sample_import.csv");
    showMessage("Sample CSV downloaded.");
  };

  const toggleRow = useCallback(id => setSelectedIds(p => ({ ...p, [id]: !p[id] })), []);
  const toggleAll = useCallback(() => {
    if (allChecked) { setSelectedIds({}); return; }
    const n = {}; rows.forEach(r => { n[r.id] = true; }); setSelectedIds(n);
  }, [allChecked, rows]);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters, search: filters.search.trim() });
    setPage(1);
  }, [filters]);
  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS }); setAppliedFilters({ ...DEFAULT_FILTERS }); setPage(1);
  }, []);

  const togglePanel = k => setPanel(p => p === k ? null : k);
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);
  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return start + i;
  });

  return (
    <div className="min-h-screen bg-slate-50/70">
      <Navbar />

      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {costBreakupRow && <CostBreakupModal row={costBreakupRow} onClose={() => setCostBreakupRow(null)} />}

      {showActions && (
        <ActionsModal
          onClose={() => setShowActions(false)}
          selectedRows={selectedRows}
          appliedFilters={appliedFilters}
          onUpdateSelected={handleUpdateSelected}
          onUploadImport={handleImportCsv}
          onUploadBulkUpdate={handleBulkUpdateCsv}
          onDownloadSample={handleDownloadSample}
          showMessage={showMessage}
        />
      )}

      <div className="max-w-[1600px] mx-auto px-5 py-6">

        {/* ── Page header ── */}
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Unit Economics</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage pricing, margins, and cost structure per SKU × marketplace</p>
          </div>
          <Button onClick={() => setShowActions(true)}>Actions</Button>
        </div>

        {/* ── Toolbar ── */}
        <div className="bg-white border border-border rounded-xl px-4 py-3 mb-3 flex items-center gap-2.5 flex-wrap">
          <Input
            className="flex-1 min-w-[220px]"
            placeholder="Search SKU, MP…"
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
          />

          <Button variant={panel === "filters" ? "default" : "outline"} size="sm" onClick={() => togglePanel("filters")}>
            {`Filters${hasActiveFilters ? " ●" : ""}`}
          </Button>

          <Button variant={panel === "columns" ? "default" : "outline"} size="sm" onClick={() => togglePanel("columns")}>
            Columns
          </Button>

          <Button variant="outline" size="sm" onClick={applyFilters}>Search</Button>

          <Button
            variant={percentMode ? "default" : "outline"}
            size="sm"
            onClick={() => setPercentMode(v => !v)}
          >
            {percentMode ? "% of SP" : "Show %"}
          </Button>

          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} / page</option>)}
          </select>
        </div>

        {/* ── Panels ── */}
        {panel === "filters" && (
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            marketplaceOptions={marketplaceOpts}
            statusOptions={statusOpts}
            onApply={applyFilters}
            onClear={clearFilters}
          />
        )}
        {panel === "columns" && (
          <ColumnPicker
            all={COLUMNS}
            visible={visibleKeys}
            onToggle={key => setVisibleKeys(p => {
              if (p.includes(key)) {
                if (p.length === 1) { showMessage("At least one column must be visible.", "warning"); return p; }
                return p.filter(k => k !== key);
              }
              return [...p, key];
            })}
            onSelectAll={() => setVisibleKeys(COLUMNS.map(c => c.key))}
            onReset={() => { setVisibleKeys(DEFAULT_VISIBLE); showMessage("Columns reset.", "success"); }}
          />
        )}

        {/* ── Table meta bar ── */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              <strong className="text-foreground">{count.toLocaleString()}</strong> total
            </span>
            {selectedRows.length > 0 && (
              <>
                <span className="text-sm text-primary font-medium">{selectedRows.length} selected</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedIds({})}>
                  Clear selection
                </Button>
              </>
            )}
          </div>
          {sort && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              Sorted by <strong className="text-foreground mx-1">{sort.key}</strong> {sort.dir}
              <button onClick={() => setSort(null)} className="ml-1 text-primary hover:underline text-xs">✕ clear</button>
            </span>
          )}
        </div>

        {/* ── Table ── */}
        <DataTable
          columns={visibleCols}
          rows={rows}
          loading={loading}
          allChecked={allChecked}
          selectedIds={selectedIds}
          onToggleAll={toggleAll}
          onToggleRow={toggleRow}
          onCellChange={handleCellChange}
          sort={sort}
          onSort={handleSort}
          colWidths={colWidths}
          onResizeStart={handleResizeStart}
          onOpenCostBreakup={setCostBreakupRow}
          percentMode={percentMode}
        />

        {/* ── Pagination ── */}
        <div className="flex items-center justify-between mt-4 py-3 flex-wrap gap-3">
          <span className="text-sm text-muted-foreground">
            Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong>
            <span className="ml-2 text-muted-foreground/60">({count.toLocaleString()} rows)</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(1)} disabled={!hasPrevPage}>«</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!hasPrevPage}>‹ Prev</Button>
            {pageNumbers.map(n => (
              <Button key={n} variant={n === page ? "default" : "outline"} size="sm" className="w-8 px-0" onClick={() => setPage(n)}>{n}</Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>Next ›</Button>
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>»</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
